import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  InputGroup,
  InputRightElement,
  Image,
  Button,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import AttendanceSummaryChart from "./attendanceSummaryChart";
import { useCategories } from "../../categorys/bookingManagement/index";
import SearchableSelect from "../../components/selectForm";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import AttendanceSummaryTable from "./attendanceSummaryTable";

// ---- helpers ----
const toMonthParam = (d) => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${yyyy}`; // backend expect "MM-YYYY"
};

const yearsToOptions = (fromYear, toYear) => {
  const opts = [];
  for (let y = toYear; y >= fromYear; y -= 1) {
    opts.push({ key: y, value: String(y) });
  }
  return opts;
};

export default function AttendanceSummary() {
  const [shouldFetch, setShouldFetch] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFetch(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);
  const { constantData } = useCategories(shouldFetch);

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({ key, value }));
  const saleBlockLists = useMemo(
    () => formatEntries(constantData?.saleBlock),
    [constantData?.saleBlock]
  );

  const saleAreaLists = useMemo(
    () => formatEntries(constantData?.saleArea),
    [constantData?.saleArea]
  );
  const supportDepartmentLists = useMemo(
    () => formatEntries(constantData?.supportDepartment),
    [constantData?.supportDepartment]
  );
  const branchLists = useMemo(
    () => formatEntries(constantData?.branch),
    [constantData?.branch]
  );

  const employeeLists = useMemo(
    () => formatEntries(constantData?.employee),
    [constantData?.employee]
  );

  // Loại nhân viên = department param (0|1|2)
  const departmentOptions = useMemo(
    () => [
      { key: 0, value: "Khối hỗ trợ kinh doanh" },
      { key: 1, value: "Khối kinh doanh" },
      // { key: 2, value: "Khối giám đốc" },
    ],
    []
  );

  // Mode thời gian: ngày / tháng / năm (chỉ chọn 1)
  const dateModeOptions = useMemo(
    () => [
      { key: "DAY", value: "Theo ngày" },
      { key: "MONTH", value: "Theo tháng" },
      { key: "YEAR", value: "Theo năm" },
    ],
    []
  );

  const [zIndex, setZIndex] = useState(1);

  // ====== FILTER STATES ======
  const [department, setDepartment] = useState(null); // param: department
  const [type, setType] = useState("ALL");
  const [saleBlockId, setSaleBlockId] = useState(null);
  const [saleAreaId, setSaleAreaId] = useState(null);
  const [supportDepartmentId, setSupportDepartmentId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  const [dateMode, setDateMode] = useState("MONTH"); // Set default to "MONTH"
  const [day, setDay] = useState(null); // Date
  const [monthDate, setMonthDate] = useState(new Date()); // Set default to current month
  const [year, setYear] = useState(new Date().getFullYear());

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return yearsToOptions(now - 5, now + 1);
  }, []);

  const resetFilters = () => {
    setDepartment(null);
    setSaleBlockId(null);
    setSaleAreaId(null);
    setSupportDepartmentId(null);
    setBranchId(null);
    setEmployeeId(null);

    setDateMode("YEAR");
    setDay(null);
    setMonthDate(null);
    setYear(new Date().getFullYear());
  };

  // Build params cho API chart
  const chartParams = useMemo(() => {
    const p = {};

    if (department !== undefined && department !== null) p.department = department;
    if (saleBlockId) p.saleBlockId = saleBlockId;
    if (saleAreaId) p.saleAreaId = saleAreaId;
    if (supportDepartmentId) p.supportDepartmentId = supportDepartmentId;
    if (branchId) p.branchId = branchId;
    if (employeeId) p.employeeId = employeeId;

    if (dateMode === "DAY") {
      if (day) {
        p.day = day;
        p.year = day.getFullYear();
      }
    } else if (dateMode === "MONTH") {
      if (monthDate) {
        p.month = toMonthParam(monthDate);
        p.year = monthDate.getFullYear();
      }
    } else {
      // YEAR
      if (year) p.year = year;
    }

    return p;
  }, [
    department,
    saleBlockId,
    saleAreaId,
    supportDepartmentId,
    branchId,
    employeeId,
    dateMode,
    day,
    monthDate,
    year,
  ]);

  return (
    <Box
      w={{ base: "100%" }}
      backgroundColor="var(--color-backgroundmain)"
      p={{ base: "24px 16px 16px" }}
    >
      <Flex alignItems={"center"} justifyContent={"space-between"}>
        <Flex alignItems={"center"} gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            Quản lý chuyên cần
          </Text>
        </Flex>
      </Flex>

      <Box mt={{ base: "23px" }}>
        {/* ===== FILTER BOX ===== */}
        <Box
          w={"full"}
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          p={"16px 16px 12px 16px"}
        >
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={{ base: "32px" }}
          >
            <Flex
              alignItems={"flex-start"}
              gap={{ base: "16px 32px" }}
              flex={"1"}
              flexDirection={"column"}
            >
              <Flex
                alignItems={"center"}
                gap={{ base: "16px 32px" }}
                flex={"1"}
                flexWrap={"wrap"}
                w={"100%"}
              >
                {/* Loại nhân viên (department) */}
                <SearchableSelect
                  isShowSearch={false}
                  options={departmentOptions}
                  initialSelectedOption={{ key: null, value: "Loại nhân viên" }}
                  setValue={setDepartment}
                />

                {/* Khối / Vùng */}
                <SearchableSelect
                  isShowSearch={true}
                  options={saleBlockLists}
                  initialSelectedOption={{ key: null, value: "Chọn khối" }}
                  setValue={setSaleBlockId}
                />
                <SearchableSelect
                  isShowSearch={true}
                  options={saleAreaLists}
                  initialSelectedOption={{ key: null, value: "Chọn vùng" }}
                  setValue={setSaleAreaId}
                />

                {/* Phòng / Chi nhánh */}
                <SearchableSelect
                  isShowSearch={true}
                  options={supportDepartmentLists}
                  initialSelectedOption={{ key: null, value: "Chọn phòng" }}
                  setValue={setSupportDepartmentId}
                />
                <SearchableSelect
                  isShowSearch={true}
                  options={branchLists}
                  initialSelectedOption={{ key: null, value: "Chọn chi nhánh" }}
                  setValue={setBranchId}
                />

                {/* Mã nhân viên */}
                <SearchableSelect
                  isShowSearch={true}
                  options={employeeLists}
                  initialSelectedOption={{ key: null, value: "Chọn nhân viên" }}
                  setValue={setEmployeeId}
                />

                {/* Mode thời gian */}
                <SearchableSelect
                  isShowSearch={false}
                  options={dateModeOptions}
                  initialSelectedOption={{ key: "MONTH", value: "Theo tháng" }}
                  setValue={(mode) => {
                    setDateMode(mode);
                    setDay(new Date());
                    setMonthDate(new Date());
                    if (mode === "YEAR" && !year)
                      setYear(new Date().getFullYear());
                  }}
                />

                {/* Date input theo mode */}
                {dateMode === "DAY" && (
                  <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
                    <DatePicker
                      selected={day}
                      onChange={(d) => setDay(d)}
                      dateFormat="dd/MM/yyyy"
                      locale="vi"
                      isClearable
                      placeholderText="Chọn ngày"
                      className="datePickerStyle"
                      onFocus={() => setZIndex(3)}
                      onBlur={() => setZIndex(1)}
                    />
                    {!day && (
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    )}
                  </InputGroup>
                )}

                {dateMode === "MONTH" && (
                  <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
                    <DatePicker
                      selected={monthDate}
                      onChange={(d) => setMonthDate(d)}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      locale="vi"
                      isClearable
                      placeholderText="Chọn tháng"
                      className="datePickerStyle"
                      onFocus={() => setZIndex(3)}
                      onBlur={() => setZIndex(1)}
                    />
                    {!monthDate && (
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    )}
                  </InputGroup>
                )}

                {dateMode === "YEAR" && (
                  <SearchableSelect
                    isShowSearch={false}
                    options={yearOptions}
                    initialSelectedOption={{
                      key: new Date().getFullYear(),
                      value: String(new Date().getFullYear()),
                    }}
                    setValue={setYear}
                  />
                )}

                {/* Reset */}
                {/* <Button
                  p={"10px 14px"}
                  borderRadius={"8px"}
                  bg={"var(--fill-avatar)"}
                  color={"var(--color-info-employee)"}
                  _hover={{ filter: "brightness(95%)" }}
                  onClick={resetFilters}
                >
                  Xóa lọc
                </Button> */}
              </Flex>
            </Flex>
          </Flex>
        </Box>

        <Flex gap={4} mt={4}>
          <AttendanceSummaryChart
            params={chartParams}
            onPickItem={(it) => {
              console.log("pick", it);
              setType(it?.type || "ALL");
            }}
          />

          <Box
            w={"full"}
            backgroundColor="var(--color-main)"
            borderRadius={{ base: "12px" }}
            p={"16px"}
            maxW={"100%"} overflow={"hidden"}
          >
            {/* Danh sách */}
            <AttendanceSummaryTable title={"Danh sách nhân viên"} param={chartParams} type={type} />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
